import { Button, ButtonSizes, ButtonVariants } from '@triumph/design-system';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ReactNode } from 'react';

import {
    ErrorMessage,
    RefetchOverlay,
    RefetchOverlayWrapper,
    RefetchSpinnerWrap,
    StateWrapper,
} from './TabDataState.StyledComponents';

const SPINNER_STYLE = { width: '1.75em', height: '1.75em', margin: 0 };
const SPINNER_STROKE_WIDTH = '4';
// Explicit px, not em — design-system's html{font-size:50%} shrinks em-based sizing, and this needs to read clearly over an already-loaded table.
const REFETCH_SPINNER_STYLE = { width: '48px', height: '48px', margin: 0 };

const DEFAULT_TEST_ID = 'tab-data-state';

interface TabDataStateProps {
    isLoading: boolean;
    isLoadingError: boolean;
    onRetry?: () => void;
    isEmpty?: boolean;
    emptyMessage?: ReactNode;
    errorMessage: ReactNode;
    testId?: string;
    children: ReactNode;
    /** Shown instead of the spinner while isLoading and there's no prior data yet — e.g. skeleton rows. */
    skeleton?: ReactNode;
    /** True while refetching with data already on screen — overlays a spinner on the existing children instead of replacing them. */
    isRefetching?: boolean;
    /** Left-align the empty/error/loading states under a section heading instead of centering them. */
    alignStart?: boolean;
}

export const TabDataState = ({
    isLoading,
    isLoadingError,
    onRetry,
    isEmpty = false,
    emptyMessage,
    errorMessage,
    testId = DEFAULT_TEST_ID,
    children,
    skeleton,
    isRefetching = false,
    alignStart = false,
}: TabDataStateProps) => {
    if (isLoadingError) {
        return (
            <StateWrapper role="alert" alignStart={alignStart} data-testid={`${testId}-error`}>
                <ErrorMessage>⚠ {errorMessage}</ErrorMessage>
                {onRetry ? (
                    <Button
                        variant={ButtonVariants.secondary}
                        size={ButtonSizes.small}
                        onClick={onRetry}
                        data-testid={`${testId}-retry`}
                    >
                        Retry
                    </Button>
                ) : null}
            </StateWrapper>
        );
    }

    if (isLoading) {
        if (skeleton) {
            return <div data-testid={`${testId}-loading`}>{skeleton}</div>;
        }

        return (
            <StateWrapper alignStart={alignStart} data-testid={`${testId}-loading`}>
                <ProgressSpinner style={SPINNER_STYLE} strokeWidth={SPINNER_STROKE_WIDTH} />
            </StateWrapper>
        );
    }

    if (isEmpty) {
        return <StateWrapper alignStart={alignStart} data-testid={`${testId}-empty`}>{emptyMessage}</StateWrapper>;
    }

    if (isRefetching) {
        return (
            <RefetchOverlayWrapper data-testid={testId}>
                {children}
                <RefetchOverlay data-testid={`${testId}-refetching`}>
                    <RefetchSpinnerWrap>
                        <ProgressSpinner
                            style={REFETCH_SPINNER_STYLE}
                            strokeWidth={SPINNER_STROKE_WIDTH}
                        />
                    </RefetchSpinnerWrap>
                </RefetchOverlay>
            </RefetchOverlayWrapper>
        );
    }

    return <>{children}</>;
};
